// components/crm/dialogs/LeadDetailsDialog.jsx
import {
  AcademicCapIcon,
  CalendarDaysIcon,
  CheckIcon,
  IdentificationIcon,
  PencilIcon,
  PhoneArrowUpRightIcon,
  UserCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, getApiErrorMessage } from '../../../lib/api';
import { formatBatchTimings, reformatLegacyTimings } from '../../../lib/batchTimings';
import { validatePhone, validateEmail } from '../../../lib/phoneEmailValidation';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import CourseSearchSelect from '../../ui/CourseSearchSelect';
import { Textarea } from '../../ui/textarea';

const QUALIFICATION_OPTIONS = [
  '10th / SSC',
  '12th / Intermediate',
  'Diploma',
  "Bachelor's Degree (B.Tech / B.E / B.Sc / BCA / B.Com / BA)",
  "Master's Degree (M.Tech / M.Sc / MCA / MBA)",
  'PhD',
  'Other',
];

const YEAR_OPTIONS = Array.from({ length: 15 }, (_, i) => String(new Date().getFullYear() - i));

const SOURCE_LABELS = {
  phone_enquiry: 'Phone Enquiry',
  website:       'Website / Google',
  social:        'Social Media',
  reference:     'Friend / Reference',
  walkin:        'Walk-in',
  other:         'Other',
};

const LEAD_STATUSES = [
  { value: 'new',                  label: 'New',                  color: 'bg-sky-100 text-sky-800' },
  { value: 'inprogress',           label: 'In Progress',          color: 'bg-blue-100 text-blue-800' },
  { value: 'attended',             label: 'Attended',             color: 'bg-indigo-100 text-indigo-800' },
  { value: 'interested',           label: 'Interested',           color: 'bg-violet-100 text-violet-800' },
  { value: 'not_interested',       label: 'Not Interested',       color: 'bg-red-100 text-red-800' },
  { value: 'enrolled_with_others', label: 'Enrolled with Others', color: 'bg-orange-100 text-orange-800' },
  { value: 'not_responding',       label: 'Not Responding',       color: 'bg-yellow-100 text-yellow-800' },
  { value: 'enrolled',             label: 'Enrolled',             color: 'bg-emerald-100 text-emerald-800' },
  { value: 'open',                 label: 'Open',                 color: 'bg-blue-100 text-blue-800' },
  { value: 'contacted',            label: 'Contacted',            color: 'bg-yellow-100 text-yellow-800' },
  { value: 'demo_scheduled',       label: 'Attending Demo',       color: 'bg-indigo-100 text-indigo-800' },
  { value: 'awaiting_decision',    label: 'Awaiting Decision',    color: 'bg-purple-100 text-purple-800' },
  { value: 'converted',            label: 'Converted',            color: 'bg-green-100 text-green-800' },
  { value: 'lost',                 label: 'Lost',                 color: 'bg-gray-100 text-gray-800' },
];

const TERMINAL_STATUSES = new Set(['converted', 'enrolled', 'lost', 'not_interested', 'enrolled_with_others']);

const getStatusConfig  = (status) => LEAD_STATUSES.find(s => s.value === status);
const getStatusColor   = (status) => getStatusConfig(status)?.color  ?? 'bg-gray-100 text-gray-800';
const getStatusLabel   = (status) => getStatusConfig(status)?.label  ?? status;

const getPriorityColor = (priority) => {
  const c = { high: 'bg-red-100 text-red-800', medium: 'bg-yellow-100 text-yellow-800', low: 'bg-green-100 text-green-800' };
  return c[priority] ?? 'bg-gray-100 text-gray-800';
};

const formatDateTime = (dateString) =>
  new Date(dateString).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const CRM_STATUS_BADGE   = 'crm-table-badge crm-table-badge--status';
const CRM_PRIORITY_BADGE = 'crm-table-badge crm-table-badge--priority';

/** Thin horizontal section divider with centred label */
const SectionLabel = ({ icon: Icon, label, color = 'text-violet-600' }) => (
  <div className="flex items-center gap-2 pt-1">
    <div className="h-px flex-1 bg-gray-100" />
    <span className={`flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 ${color}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </span>
    <div className="h-px flex-1 bg-gray-100" />
  </div>
);

/** Key-value pair used in view mode */
const InfoRow = ({ label, children }) => (
  <div>
    <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
    <div className="text-sm font-medium text-gray-800">{children}</div>
  </div>
);

export default function LeadDetailsDialog({ lead, open, onOpenChange, courses, coursesLoading = false, users, onLeadUpdated }) {
  const [isEditMode, setIsEditMode]     = useState(false);
  const [selectedLead, setSelectedLead] = useState(lead);
  const [batches, setBatches]           = useState([]);

  const [newNote, setNewNote] = useState({
    content: '', statusUpdate: '', createTask: false,
    taskDueDate: '', taskPriority: 'medium', assignedTo: '',
  });

  const [editingNoteId,   setEditingNoteId]   = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [savingNote,      setSavingNote]      = useState(false);
  const [updatingLead,    setUpdatingLead]    = useState(false);
  const [addingNote,      setAddingNote]      = useState(false);

  useEffect(() => {
    setSelectedLead(lead);
    setEditingNoteId(null);
    setEditingNoteText('');
    setIsEditMode(false);
  }, [lead]);

  const currentCourseId = selectedLead?.interestCourse?._id || selectedLead?.interestCourse;
  useEffect(() => {
    if (!currentCourseId) { setBatches([]); return; }
    api.get('/batches', { params: { courseId: currentCourseId, limit: 100 } })
      .then(r => {
        const all = r.data?.batches || r.data?.data || (Array.isArray(r.data) ? r.data : []);
        setBatches(all.filter(b => {
          const id = b.courseId?._id || b.courseId;
          return String(id) === String(currentCourseId);
        }));
      })
      .catch(() => setBatches([]));
  }, [currentCourseId]);

  // Reformat batchTimings whenever the batch selection or loaded batches change.
  // Runs in both view and edit mode so the timing banner always shows the new format.
  useEffect(() => {
    const batchId = selectedLead?.interestBatch?._id || selectedLead?.interestBatch;
    if (!batchId || batches.length === 0) return;
    const batch = batches.find(b => b._id === batchId);
    if (batch?.schedule?.length) {
      const formatted = formatBatchTimings(batch.schedule);
      if (formatted) setSelectedLead(l => ({ ...l, batchTimings: formatted }));
    }
  // eslint-disable-next-line
  }, [selectedLead?.interestBatch, batches]);

  // ── API handlers ─────────────────────────────────────────────────────────────

  const handleUpdateLead = async () => {
    const phoneResult = validatePhone(selectedLead.phone);
    if (!phoneResult.valid) { toast.error(phoneResult.message); return; }

    const emailResult = validateEmail(selectedLead.email);
    if (!emailResult.valid) { toast.error(emailResult.message); return; }

    setUpdatingLead(true);
    try {
      await api.put(`/leads/${selectedLead._id}`, {
        name:                selectedLead.name,
        email:               emailResult.cleaned || undefined,
        phone:               phoneResult.cleaned,
        interestCourse:      selectedLead.interestCourse?._id || selectedLead.interestCourse,
        interestBatch:       selectedLead.interestBatch?._id  || selectedLead.interestBatch  || undefined,
        source:              selectedLead.source,
        status:              selectedLead.status,
        priority:            selectedLead.priority,
        followUpDate:        selectedLead.followUpDate,
        owner:               selectedLead.owner?._id || selectedLead.owner || undefined,
        qualification:       selectedLead.qualification       || undefined,
        yearOfPassedOut:     selectedLead.yearOfPassedOut     || undefined,
        batchTimings:        selectedLead.batchTimings        || undefined,
        preferredMode:       selectedLead.preferredMode       || undefined,
        referencePersonName: selectedLead.referencePersonName || undefined,
        courseInterestText:  selectedLead.courseInterestText  || undefined,
      });
      toast.success('Lead updated successfully!');
      setIsEditMode(false);
      onLeadUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update lead'));
    } finally {
      setUpdatingLead(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.content.trim()) { toast.error('Please enter note content'); return; }
    if (newNote.createTask && !newNote.taskDueDate) {
      toast.error('Please set a due date for the follow-up task');
      return;
    }
    setAddingNote(true);
    try {
      // Only send note-relevant fields to the notes endpoint
      const res = await api.post(`/leads/${selectedLead._id}/notes`, {
        content: newNote.content.trim(),
      });

      if (newNote.statusUpdate && newNote.statusUpdate !== selectedLead.status) {
        await api.put(`/leads/${selectedLead._id}`, {
          name:                selectedLead.name,
          email:               selectedLead.email,
          phone:               selectedLead.phone,
          interestCourse:      selectedLead.interestCourse?._id || selectedLead.interestCourse,
          interestBatch:       selectedLead.interestBatch?._id  || selectedLead.interestBatch  || undefined,
          source:              selectedLead.source,
          priority:            selectedLead.priority,
          followUpDate:        selectedLead.followUpDate,
          status:              newNote.statusUpdate,
          qualification:       selectedLead.qualification       || undefined,
          yearOfPassedOut:     selectedLead.yearOfPassedOut     || undefined,
          batchTimings:        selectedLead.batchTimings        || undefined,
          preferredMode:       selectedLead.preferredMode       || undefined,
          referencePersonName: selectedLead.referencePersonName || undefined,
          courseInterestText:  selectedLead.courseInterestText  || undefined,
        });
      }

      // Create follow-up task if requested
      if (newNote.createTask) {
        await api.post('/crm/tasks', {
          leadId:     selectedLead._id,
          taskType:   'follow_up',
          dueDate:    newNote.taskDueDate,
          priority:   newNote.taskPriority || 'medium',
          remarks:    newNote.content.trim(),
          assignedTo: newNote.assignedTo || undefined,
        });
      }

      const messages = ['Note added successfully!'];
      if (newNote.statusUpdate && newNote.statusUpdate !== selectedLead.status) messages.push('Status updated.');
      if (newNote.createTask) messages.push('Follow-up task created.');
      toast.success(messages.join(' '));

      setNewNote({ content: '', statusUpdate: '', createTask: false, taskDueDate: '', taskPriority: 'medium', assignedTo: '' });
      if (res.data?.data) setSelectedLead(res.data.data);
      onLeadUpdated();
      onOpenChange(false);
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error(getApiErrorMessage(error, 'Failed to add note'));
    } finally {
      setAddingNote(false);
    }
  };

  const handleSaveNoteEdit = async (noteId) => {
    if (!noteId) { toast.error('Invalid note — refresh and try again'); return; }
    if (!editingNoteText.trim()) { toast.error('Note cannot be empty'); return; }
    try {
      setSavingNote(true);
      const leadId = selectedLead._id ?? selectedLead.id;
      const res = await api.put(
        `/leads/${encodeURIComponent(leadId)}/notes/${encodeURIComponent(String(noteId))}`,
        { content: editingNoteText.trim() }
      );
      toast.success('Note updated successfully!');
      setEditingNoteId(null);
      setEditingNoteText('');
      if (res.data?.data) setSelectedLead(res.data.data);
      onLeadUpdated();
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update note'));
    } finally {
      setSavingNote(false);
    }
  };

  if (!selectedLead) return null;

  const isTerminal = TERMINAL_STATUSES.has(selectedLead.status);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-[620px] md:max-w-[720px] lg:max-w-[800px] max-h-[92vh] p-0 gap-0 [&>button:last-child]:hidden">

        {/* ── Gradient Header ──────────────────────────────────────────────────── */}
        <div className={`px-6 py-4 flex-shrink-0 ${isEditMode
          ? 'bg-gradient-to-r from-amber-500 to-orange-500'
          : 'bg-gradient-to-r from-violet-600 to-purple-600'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <UserCircleIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-white leading-tight">{selectedLead.name}</h2>
                <p className="text-xs text-white/70 mt-0.5 truncate max-w-[300px]">
                  {selectedLead.email} {selectedLead.email && selectedLead.phone ? '·' : ''} {selectedLead.phone}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 flex-shrink-0">
              {!isEditMode ? (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setIsEditMode(true)}
                    className="h-8 px-3 text-xs bg-white/20 hover:bg-white/30 text-white border-0 gap-1"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Edit</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onOpenChange(false)}
                    className="h-8 px-3 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
                  >
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => { setIsEditMode(false); setSelectedLead(lead); }}
                    disabled={updatingLead}
                    className="h-8 px-3 text-xs bg-white/20 hover:bg-white/30 text-white border-0"
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleUpdateLead}
                    disabled={updatingLead}
                    className="h-8 px-3 text-xs bg-white text-amber-700 hover:bg-white/90 border-0 gap-1 font-semibold"
                  >
                    {updatingLead ? (
                      <>
                        <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Saving…
                      </>
                    ) : (
                      <>
                        <CheckIcon className="h-3.5 w-3.5" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Scrollable Body ───────────────────────────────────────────────────── */}
        <div className="overflow-y-auto overflow-x-hidden px-6 py-5 space-y-5 max-h-[calc(92vh-80px)]">

          {/* ── EDIT MODE ────────────────────────────────────────────────────── */}
          {isEditMode ? (
            <div className="space-y-4">

              {/* Contact */}
              <SectionLabel icon={IdentificationIcon} label="Contact Information" color="text-amber-600" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Name</Label>
                  <Input className="h-9 text-sm" value={selectedLead.name}
                    onChange={(e) => setSelectedLead({ ...selectedLead, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Phone</Label>
                  <Input className="h-9 text-sm" type="text" placeholder="9876543210 or 9876543210/8765432109" value={selectedLead.phone}
                    onChange={(e) => setSelectedLead({ ...selectedLead, phone: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-medium text-gray-700">Email</Label>
                  <Input className="h-9 text-sm" type="text" placeholder="name@example.com or email1/email2" value={selectedLead.email || ''}
                    onChange={(e) => setSelectedLead({ ...selectedLead, email: e.target.value })} />
                </div>
              </div>

              {/* Academic */}
              <SectionLabel icon={AcademicCapIcon} label="Academic Background" color="text-amber-600" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Qualification</Label>
                  <Select value={selectedLead.qualification || ''}
                    onValueChange={v => setSelectedLead({ ...selectedLead, qualification: v })}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {QUALIFICATION_OPTIONS.map(q => <SelectItem key={q} value={q} className="text-sm">{q}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Year of Passed Out</Label>
                  <Input
                    className="h-9 text-sm"
                    type="number"
                    min="1980"
                    max={new Date().getFullYear()}
                    placeholder="e.g. 2022"
                    value={selectedLead.yearOfPassedOut || ''}
                    onChange={e => setSelectedLead({ ...selectedLead, yearOfPassedOut: e.target.value })}
                  />
                </div>
              </div>

              {/* Course & Batch */}
              <SectionLabel icon={PhoneArrowUpRightIcon} label="Course Interest" color="text-amber-600" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Interested Course</Label>
                  <CourseSearchSelect
                    courses={courses}
                    loading={coursesLoading}
                    value={selectedLead.interestCourse?._id || selectedLead.interestCourse || ''}
                    onChange={v => setSelectedLead({ ...selectedLead, interestCourse: v, interestBatch: '', batchTimings: '' })}
                    placeholder="Select course"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Batch</Label>
                  <Select
                    value={selectedLead.interestBatch?._id || selectedLead.interestBatch || ''}
                    onValueChange={v => setSelectedLead({ ...selectedLead, interestBatch: v })}
                    disabled={batches.length === 0}
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue placeholder={batches.length === 0 ? 'No batches' : 'Select batch'} />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map(b => (
                        <SelectItem key={b._id} value={b._id} className="text-sm">{b.name} — {b.mode}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedLead.batchTimings && (
                  <div className="sm:col-span-2 flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5">
                    <span>🕐</span>
                    <span className="text-xs font-semibold text-blue-700 uppercase">Timings: </span>
                    <span className="text-blue-800 text-sm">{reformatLegacyTimings(selectedLead.batchTimings)}</span>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Preferred Mode</Label>
                  <div className="flex gap-2">
                    {[
                      { value: 'offline', icon: '🏫', label: 'Offline' },
                      { value: 'online',  icon: '💻', label: 'Online'  },
                      { value: 'weekend', icon: '📅', label: 'Weekend' },
                    ].map(({ value, icon, label }) => (
                      <label
                        key={value}
                        className={`flex-1 flex items-center justify-center gap-1.5 border-2 rounded-lg py-2 cursor-pointer text-sm font-medium transition-all ${
                          (selectedLead.preferredMode || '') === value
                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                            : 'border-gray-200 text-gray-600 hover:border-amber-300'
                        }`}
                      >
                        <input type="radio" name="editLeadMode" value={value} checked={(selectedLead.preferredMode || '') === value} onChange={() => setSelectedLead({ ...selectedLead, preferredMode: value })} className="sr-only" />
                        {icon} {label}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Reference Person</Label>
                  <Input className="h-9 text-sm" value={selectedLead.referencePersonName || ''}
                    onChange={(e) => setSelectedLead({ ...selectedLead, referencePersonName: e.target.value })} />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-medium text-gray-700">
                    Course Interest (free text)
                    <span className="ml-1 text-gray-400 font-normal">— from public form / manual note</span>
                  </Label>
                  <Input
                    className="h-9 text-sm"
                    placeholder="e.g. SAP ABAP, Full Stack Java…"
                    value={selectedLead.courseInterestText || ''}
                    onChange={(e) => setSelectedLead({ ...selectedLead, courseInterestText: e.target.value })}
                  />
                </div>
              </div>

              {/* CRM Fields */}
              <SectionLabel icon={IdentificationIcon} label="Lead Details" color="text-amber-600" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Source</Label>
                  <Select value={selectedLead.source || 'other'}
                    onValueChange={v => setSelectedLead({ ...selectedLead, source: v })}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="phone_enquiry" className="text-sm">📞 Phone Enquiry</SelectItem>
                      <SelectItem value="website"       className="text-sm">🌐 Website / Google</SelectItem>
                      <SelectItem value="social"        className="text-sm">📱 Social Media</SelectItem>
                      <SelectItem value="reference"     className="text-sm">👥 Friend / Reference</SelectItem>
                      <SelectItem value="walkin"        className="text-sm">🚶 Walk-in</SelectItem>
                      <SelectItem value="other"         className="text-sm">✦ Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Status</Label>
                  <Select value={selectedLead.status || 'new'}
                    onValueChange={v => setSelectedLead({ ...selectedLead, status: v })}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new"                  className="text-sm">New</SelectItem>
                      <SelectItem value="open"                 className="text-sm">Open</SelectItem>
                      <SelectItem value="contacted"            className="text-sm">Contacted</SelectItem>
                      <SelectItem value="inprogress"           className="text-sm">In Progress</SelectItem>
                      <SelectItem value="demo_scheduled"       className="text-sm">Attending Demo</SelectItem>
                      <SelectItem value="attended"             className="text-sm">Attended</SelectItem>
                      <SelectItem value="awaiting_decision"    className="text-sm">Awaiting Decision</SelectItem>
                      <SelectItem value="interested"           className="text-sm">Interested</SelectItem>
                      <SelectItem value="not_interested"       className="text-sm">Not Interested</SelectItem>
                      <SelectItem value="enrolled_with_others" className="text-sm">Enrolled with Others</SelectItem>
                      <SelectItem value="not_responding"       className="text-sm">Not Responding</SelectItem>
                      <SelectItem value="enrolled"             className="text-sm">Enrolled</SelectItem>
                      <SelectItem value="converted"            className="text-sm text-gray-400">Converted (legacy)</SelectItem>
                      <SelectItem value="lost"                 className="text-sm text-gray-400">Lost (legacy)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Priority</Label>
                  <Select value={selectedLead.priority}
                    onValueChange={v => setSelectedLead({ ...selectedLead, priority: v })}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high"   className="text-sm">🔴 High</SelectItem>
                      <SelectItem value="medium" className="text-sm">🟡 Medium</SelectItem>
                      <SelectItem value="low"    className="text-sm">🟢 Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Lead Owner</Label>
                  <Select
                    value={selectedLead.owner?._id || selectedLead.owner || ''}
                    onValueChange={(v) => setSelectedLead(l => ({ ...l, owner: { _id: v, name: users.find(u => u._id === v)?.name || '' } }))}
                  >
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select staff member" /></SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u._id} value={u._id} className="text-sm">{u.name} ({u.role})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs font-medium text-gray-700">Follow Up Date</Label>
                  <Input className="h-9 text-sm" type="datetime-local"
                    value={selectedLead.followUpDate ? selectedLead.followUpDate.split('.')[0] : ''}
                    onChange={(e) => setSelectedLead({ ...selectedLead, followUpDate: e.target.value })} />
                </div>
              </div>
            </div>

          ) : (
            /* ── VIEW MODE ──────────────────────────────────────────────────── */
            <div className="space-y-5">

              {/* Public enquiry badge */}
              {selectedLead.isPublicSubmission && (
                <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                  <span className="text-xs font-semibold text-violet-700 uppercase tracking-wide">🌐 Public Enquiry</span>
                  <span className="text-xs text-violet-600">Submitted via the online enquiry form</span>
                </div>
              )}

              {/* Status / Priority chips */}
              <div className="flex flex-wrap gap-2">
                <Badge className={`${CRM_STATUS_BADGE} ${getStatusColor(selectedLead.status)}`}>
                  {getStatusLabel(selectedLead.status)}
                </Badge>
                <Badge className={`${CRM_PRIORITY_BADGE} capitalize ${getPriorityColor(selectedLead.priority)}`}>
                  {selectedLead.priority} priority
                </Badge>
              </div>

              {/* Course Interest */}
              <div>
                <SectionLabel icon={PhoneArrowUpRightIcon} label="Course Interest" />
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InfoRow label="Interested Course">{selectedLead.interestCourse?.name || '—'}</InfoRow>
                  <InfoRow label="Preferred Batch">{selectedLead.interestBatch?.name || '—'}</InfoRow>
                  {selectedLead.batchTimings && (
                    <div className="sm:col-span-2 flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5">
                      <span>🕐</span>
                      <div>
                        <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Batch Timings: </span>
                        <span className="text-sm text-blue-800">{reformatLegacyTimings(selectedLead.batchTimings)}</span>
                      </div>
                    </div>
                  )}
                  <InfoRow label="Preferred Mode">
                    <span className="capitalize">{selectedLead.preferredMode || '—'}</span>
                  </InfoRow>
                  <InfoRow label="Lead Source">{SOURCE_LABELS[selectedLead.source] || selectedLead.source || '—'}</InfoRow>
                  {selectedLead.courseInterestText && (
                    <div className="sm:col-span-2">
                      <InfoRow label="Course Interest (text)">{selectedLead.courseInterestText}</InfoRow>
                    </div>
                  )}
                </div>
              </div>

              {/* Academic */}
              <div>
                <SectionLabel icon={AcademicCapIcon} label="Academic Background" />
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InfoRow label="Qualification">{selectedLead.qualification || '—'}</InfoRow>
                  <InfoRow label="Year of Passed Out">{selectedLead.yearOfPassedOut || '—'}</InfoRow>
                  <InfoRow label="Reference Person">{selectedLead.referencePersonName || '—'}</InfoRow>
                  <InfoRow label="Assigned Owner">{selectedLead.owner?.name || 'System'}</InfoRow>
                </div>
              </div>

              {/* Follow-up */}
              <div>
                <SectionLabel icon={CalendarDaysIcon} label="Follow-up" />
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">Next Follow Up</p>
                    {selectedLead.followUpDate ? (
                      <div className="flex flex-col gap-1">
                        <p className={`text-sm font-medium ${new Date(selectedLead.followUpDate) < new Date() ? 'text-red-600' : 'text-gray-800'}`}>
                          {formatDateTime(selectedLead.followUpDate)}
                        </p>
                        {new Date(selectedLead.followUpDate) < new Date() && (
                          <span className="crm-badge-overdue w-fit">Overdue</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm font-medium text-gray-400">Not set</p>
                    )}
                  </div>
                  {selectedLead.convertedAt && (
                    <InfoRow label="Converted At">{formatDateTime(selectedLead.convertedAt)}</InfoRow>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Notes Section ─────────────────────────────────────────────────── */}
          <div className="border-t border-gray-100 pt-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <span>Notes &amp; Activity</span>
              <span className="text-xs font-normal text-gray-400">({selectedLead.notes?.length || 0})</span>
            </h3>

            {/* Existing notes */}
            <div className="space-y-2">
              {selectedLead.notes?.length > 0 ? (
                selectedLead.notes.map((note) => {
                  const noteKey = note._id ?? note.id;
                  return (
                    <Card key={noteKey || String(note.content).slice(0, 20)} className="shadow-sm border-gray-100">
                      <CardContent className="p-3">
                        {editingNoteId === noteKey ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editingNoteText}
                              onChange={(e) => setEditingNoteText(e.target.value)}
                              rows={4}
                              className="text-sm"
                            />
                            <div className="flex gap-2 justify-end">
                              <Button type="button" variant="outline" size="sm" className="h-8 text-xs" disabled={savingNote}
                                onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }}>
                                <XMarkIcon className="h-3.5 w-3.5 mr-1" />Cancel
                              </Button>
                              <Button type="button" size="sm" className="h-8 text-xs" disabled={savingNote}
                                onClick={() => handleSaveNoteEdit(noteKey)}>
                                <CheckIcon className="h-3.5 w-3.5 mr-1" />Save
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm whitespace-pre-wrap text-gray-700">{note.content}</p>
                            <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
                              <p className="text-xs text-gray-400">
                                By {note.createdBy?.name || 'System'} · {formatDateTime(note.createdAt)}
                                {note.updatedAt && <span className="ml-1">· edited {formatDateTime(note.updatedAt)}</span>}
                              </p>
                              <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs"
                                onClick={() => { setEditingNoteId(noteKey); setEditingNoteText(note.content); }}>
                                <PencilIcon className="h-3.5 w-3.5 mr-1" />Edit
                              </Button>
                            </div>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <p className="text-sm text-gray-400 text-center py-6 bg-gray-50 rounded-lg">No notes yet</p>
              )}
            </div>

            {/* Add note form — always visible */}
            <Card className="shadow-sm border-gray-100">
              <CardHeader className="p-3 pb-2">
                <CardTitle className="text-sm text-gray-700">Add Note</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5 p-3 pt-0">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-600">Note content</Label>
                  <Textarea
                    value={newNote.content}
                    onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                    rows={3}
                    placeholder="Enter your notes here…"
                    className="text-sm resize-none"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-600">Update status <span className="text-gray-400">(optional)</span></Label>
                  <Select
                    value={newNote.statusUpdate || '__no_change__'}
                    onValueChange={(value) => setNewNote({ ...newNote, statusUpdate: value === '__no_change__' ? '' : value })}
                  >
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__no_change__" className="text-sm text-gray-400">— Keep current status —</SelectItem>
                      {LEAD_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value} className="text-sm">{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="createTask"
                    checked={newNote.createTask}
                    onChange={(e) => setNewNote({ ...newNote, createTask: e.target.checked })}
                    className="rounded border-gray-300 accent-violet-600"
                  />
                  <Label htmlFor="createTask" className="text-xs cursor-pointer">Create follow-up task</Label>
                </div>

                {newNote.createTask && (
                  <div className="space-y-2.5 border-t pt-2.5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-600">Due date</Label>
                        <Input type="datetime-local" className="h-9 text-sm"
                          value={newNote.taskDueDate}
                          onChange={(e) => setNewNote({ ...newNote, taskDueDate: e.target.value })} />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-600">Priority</Label>
                        <Select value={newNote.taskPriority}
                          onValueChange={(value) => setNewNote({ ...newNote, taskPriority: value })}>
                          <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="high"   className="text-sm">🔴 High</SelectItem>
                            <SelectItem value="medium" className="text-sm">🟡 Medium</SelectItem>
                            <SelectItem value="low"    className="text-sm">🟢 Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-gray-600">Assign to</Label>
                      <Select value={newNote.assignedTo}
                        onValueChange={(value) => setNewNote({ ...newNote, assignedTo: value })}>
                        <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select user" /></SelectTrigger>
                        <SelectContent>
                          {users.map((user) => (
                            <SelectItem key={user._id} value={user._id} className="text-sm">
                              {user.name} — {user.role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                <Button onClick={handleAddNote} size="sm" disabled={addingNote}
                  className="w-full text-sm bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white">
                  {addingNote ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Saving…
                    </span>
                  ) : 'Add Note'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
